import { Empty } from '@components'
import { Spinner } from '@components/ui/Primitives'
import { motion } from 'framer-motion'
import React from 'react'
import styled, { css } from 'styled-components'

interface AnimatedContentLoaderProps {
  isLoading: boolean
  isEmpty?: boolean
  isEmptyDescription?: string | React.ReactNode
  isEmptyContent?: React.ReactNode
  contentDelay?: number
  spinnerSize?: number
  /** When set, shown instead of the default spinner while loading. */
  loadingContent?: React.ReactNode
  disableContentAnimation?: boolean
  contentDisplay?: 'block' | 'flex'
  children: React.ReactNode
  isError?: boolean
  errorDescription?: string | React.ReactNode
}

export const AnimatedContentLoader: React.FC<AnimatedContentLoaderProps> = ({
  isLoading,
  children,
  isEmpty = false,
  isEmptyContent = null,
  isEmptyDescription = '',
  spinnerSize = 24,
  loadingContent,
  contentDisplay = 'block',
  isError = false,
  errorDescription = ''
}) => {
  if (isError && !errorDescription) {
    throw new Error('AnimatedContentLoader: errorDescription must be provided when isError is true')
  }

  const showIsEmpty = !isLoading && isEmpty
  const showError = !isLoading && isError
  const showContent = !isLoading && !showIsEmpty && !showError

  return (
    <>
      {isLoading && (
        <AnimatedElement key="spinner" className="spinner-wrapper">
          {loadingContent ?? (
            <SpinnerWrapper>
              <Spinner margin={32} alignSelf="center" size={spinnerSize} />
            </SpinnerWrapper>
          )}
        </AnimatedElement>
      )}

      {showIsEmpty && !showError && (
        <AnimatedElement key="empty">
          {isEmptyContent ? (
            isEmptyContent
          ) : typeof isEmptyDescription === 'string' ? (
            <Empty text={isEmptyDescription} />
          ) : (
            isEmptyDescription
          )}
        </AnimatedElement>
      )}

      {showError && (
        <AnimatedElement key="error">
          {typeof errorDescription === 'string' ? (
            <Empty text={errorDescription} />
          ) : (
            errorDescription
          )}
        </AnimatedElement>
      )}

      {showContent && (
        <AnimatedElement key="loaded-content" $contentDisplay={contentDisplay}>
          {children}
        </AnimatedElement>
      )}
    </>
  )
}

const AnimatedElement = styled(motion.div).attrs({
  initial: { opacity: 0, y: 0 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 0 },
  transition: { type: 'tween' }
})<{ $contentDisplay?: 'block' | 'flex' }>`
  overflow: visible;
  width: 100%;
  height: 100%;
  ${({ $contentDisplay = 'block' }) =>
    $contentDisplay === 'flex'
      ? css`
          display: flex;
          flex: 1;
          flex-direction: column;
          min-height: 0;
          min-width: 0;
        `
      : css`
          display: block;
        `}
`

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
`
